// As there is no differentiator for NewCopyForm, that is stable, index was used
/* eslint-disable react/no-array-index-key */

import { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Switch,
  Box,
  Grid,
  Button,
  LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import TargetGroupSelect from './TargetGroupSelect/TargetGroupSelect';
import WeeklyWeightSlider from './WeeklyWeightSlider/WeeklyWeightSlider';
import InfoBox from './InfoBox/InfoBox';
import NewCopyForm from './NewCopyForm/NewCopyForm';
import AddNewCopy from './AddNewCopy/AddNewCopy';
import GrpSettingsAccordion from './GrpSettingsAccordion/GrpSettingsAccordion';
import GrpPercentageSettingsAccordion from './GrpPercentageSettingsAccordion/GrpPercentageSettingsAccordion';
import { transformDataset } from '../utils/transformDataset';
import WeeklyDatePicker from '../../../../../common/WeeklyDatePicker/WeeklyDatePicker';
import usePlanStore, {
  GRPTable,
  TVConfig,
} from '../../../../../../../../store/planStore';
import useAdvertisers from '../hooks/useAdvertisers';
import useCopyLengths from '../hooks/useCopyLengths';
import useTVSetup from '../hooks/useTVSetup';
import checkCampaignLengthValidity from './utils/checkCampaignLengthValidity';
import WarningBox from './WarningBox/WarningBox';
import HelperTooltip from '../../../../../../../../shared/HelperTooltip/HelperTooltip';
import linksConfig from '../../../../../../../../config/linking';

export default function TVAccordion() {
  const { t } = useTranslation();
  const {
    countryForms,
    selectedCountry,
    updateTVConfig,
    addCopyFormEntry,
    removeCopyFormEntry,
    resetCopyFormEntry,
    saveTVConfig,
  } = usePlanStore();

  const tvConfig = countryForms[selectedCountry!]?.offline?.tv;

  const formData = countryForms[selectedCountry!]?.offline;

  const { data: fetchedAdvertisers, isFetching: isAdvertisersFetching } =
    useAdvertisers(selectedCountry ?? '');
  const { data: fetchedCopyLengths, isFetching: isCopyLengthsFetching } =
    useCopyLengths();
  const {
    fetchTVSetup,
    data: fetchedTVSetup,
    isLoading: isTVSetupFetching,
    error: isSetupError,
  } = useTVSetup(selectedCountry!);

  useEffect(() => {
    if (!countryForms[selectedCountry!].offline?.tv?.targetGroup) {
      fetchTVSetup(undefined);
    }
  }, [countryForms[selectedCountry!].offline?.tv?.targetGroup]);

  const copyForm = formData?.tv?.copyForm;

  const handleAddNewCopy = (index: number) => {
    addCopyFormEntry(selectedCountry ?? '', index);
  };

  const handleRemoveCopyFormEntry = (indexToRemove: number) => {
    if (!selectedCountry || !copyForm) return;

    removeCopyFormEntry(selectedCountry, indexToRemove);

    const updatedCopyForm =
      usePlanStore.getState().countryForms[selectedCountry]?.offline?.tv
        ?.copyForm || [];

    const updatedCopyFormWithNewIds = updatedCopyForm.map((entry, newIndex) => {
      if (!entry.id) return entry;

      const idParts = entry.id.split('_');

      if (idParts.length > 3) {
        idParts[3] = newIndex.toString();
      } else {
        idParts[2] = newIndex.toString();
      }
      return { ...entry, id: idParts.join('_') };
    });
    updateTVConfig(selectedCountry, { copyForm: updatedCopyFormWithNewIds });
  };

  const handleSetGrpsButtonClick = () => {
    if (
      formData?.tv?.startDate &&
      formData?.tv.endDate &&
      formData?.tv.weeklyWeight
    ) {
      const newGrpTable = transformDataset(
        moment(formData.tv.startDate, 'DD-MM-YYYY'),
        moment(formData.tv.endDate, 'DD-MM-YYYY'),
        formData.tv.copyForm
      );
      saveTVConfig(selectedCountry!);
      updateTVConfig(selectedCountry!, { grpTable: newGrpTable as GRPTable });
    }
  };

  const extractRelevantFields = (config: Partial<TVConfig> | undefined) => {
    if (!config) return null;
    const {
      copyForm: checkedCopyForm,
      startDate: checkedStartDate,
      endDate: checkedEndDate,
      isAvailable,
      maxCampaignLength,
      minCampaignLength,
      recommendedCampaignLength,
      targetGroup,
      weeklyWeight,
    } = config;

    return {
      copyForm: checkedCopyForm,
      startDate: checkedStartDate,
      endDate: checkedEndDate,
      isAvailable,
      maxCampaignLength,
      minCampaignLength,
      recommendedCampaignLength,
      targetGroup,
      weeklyWeight,
    };
  };

  const relevantTVConfig = useMemo(
    () => extractRelevantFields(tvConfig),
    [tvConfig]
  );
  const relevantSavedTVConfig = useMemo(
    () =>
      extractRelevantFields(
        countryForms[selectedCountry!]?.offline?.savedTVConfig
      ),
    [countryForms, selectedCountry]
  );

  const isTVConfigChanged =
    JSON.stringify(relevantTVConfig) !== JSON.stringify(relevantSavedTVConfig);

  const handleChangeStateValues = (key: keyof TVConfig, value: number) => {
    if (!selectedCountry) return;

    const formattedValue =
      moment.isMoment(value) || typeof value === 'object'
        ? moment(value).format('DD-MM-YYYY')
        : value;

    updateTVConfig(selectedCountry, { [key]: formattedValue });
  };

  if (!selectedCountry) {
    return null;
  }

  return (
    <form>
      <Accordion
        defaultExpanded
        sx={{
          '& .MuiAccordionSummary-root': {
            backgroundColor: 'common.white',
            alignItems: 'center',
            height: '48px',
            borderBottom: '1px solid #C7D8F9',
            borderTop: '1px solid #C7D8F9',
            pl: 0,
            mt: '24px',
            '& .MuiAccordionSummary-content': {
              textAlign: 'center',
              height: 'initial',
              pl: '21px',
            },
            '& .MuiAccordionSummary-expandIconWrapper': {
              marginRight: 0,
            },
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            '&.Mui-expanded': {
              minHeight: '48px',
            },
            '.MuiAccordionSummary-content': {
              height: '48px',
              margin: 0,
              pl: '6px',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ fontWeight: 700 }}>TV</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ background: '#F6F6F6' }}>
          {isTVSetupFetching && !formData?.tv?.targetGroup ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 120,
              }}
            >
              <LinearProgress sx={{ width: '50%' }} />
            </Box>
          ) : null}
          {isSetupError ? (
            <Grid
              container
              spacing="24px"
              mt={0}
              flexDirection="column"
              alignItems="center"
            >
              <Typography variant="body1" color="error" fontWeight={700}>
                Data Not Found
              </Typography>
              <Typography variant="body2" color="error">
                We couldn&apos;t locate the necessary data in our database to
                generate plan estimates.
                <br />
                Consequently, saving the data plan you&apos;re currently
                creating isn&apos;t possible.
                <br />
                Please contact your Social Media Team to verify the data.
              </Typography>
            </Grid>
          ) : (
            <>
              <Grid container spacing="24px" mt={0}>
                {!(isTVSetupFetching && !formData?.tv?.targetGroup) && (
                  <Grid item xs={3}>
                    <TargetGroupSelect fetchTVSetup={fetchTVSetup} />
                  </Grid>
                )}
                {formData?.tv?.targetGroup && (
                  <>
                    <Grid item xs={3} display="flex" alignItems="center">
                      {isTVSetupFetching ? (
                        <LinearProgress sx={{ width: '100%' }} />
                      ) : (
                        <WeeklyWeightSlider
                          weeklyWeight={formData?.tv?.weeklyWeight}
                          tvTouchpointSetup={formData?.tv}
                        />
                      )}
                    </Grid>
                    <Grid item xs={3}>
                      <HelperTooltip
                        tooltipData={linksConfig.checkCompetitorsWeights}
                        sx={{ marginLeft: '12px', alignSelf: 'flex-start' }}
                      />
                    </Grid>
                    <Grid item xs={3} />
                    {isTVSetupFetching ? (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: '12px',
                        }}
                      >
                        <LinearProgress sx={{ width: '100%' }} />
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={3}>
                          <WeeklyDatePicker
                            label={t('mediaPlanning.startDate')}
                            value={formData?.tv?.startDate}
                            setState={(value) =>
                              handleChangeStateValues('startDate', value)
                            }
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <WeeklyDatePicker
                            label={t('mediaPlanning.endDate')}
                            value={formData?.tv?.endDate}
                            setState={(value) =>
                              handleChangeStateValues('endDate', value)
                            }
                          />
                        </Grid>
                        <Grid item xs={6} display="flex" alignItems="center">
                          <InfoBox />
                        </Grid>
                      </>
                    )}
                    {formData?.tv?.startDate &&
                      formData.tv.endDate &&
                      !checkCampaignLengthValidity(
                        formData?.tv?.startDate,
                        formData?.tv?.endDate
                      ) && (
                        <Grid item xs={12}>
                          <WarningBox text="mediaPlanning.warnings.tooShortCampaignInGivenMonth" />
                        </Grid>
                      )}
                    {copyForm?.map((entry, index) => (
                      <NewCopyForm
                        index={index}
                        key={`${index}-copy-form`}
                        totalRows={copyForm.length}
                        onRemove={() => handleRemoveCopyFormEntry(index)}
                        onReset={() =>
                          resetCopyFormEntry(selectedCountry!, index)
                        }
                        fetchedAdvertisers={fetchedAdvertisers.map(
                          ({ value, name }) => ({
                            id: value,
                            title: name,
                          })
                        )}
                        isAdvertisersFetching={isAdvertisersFetching}
                        fetchedCopyLengths={fetchedCopyLengths}
                        isCopyLengthsFetching={isCopyLengthsFetching}
                      />
                    ))}

                    <Grid item xs={12} mb="32px">
                      <AddNewCopy
                        onAddNewCopy={handleAddNewCopy}
                        index={copyForm?.length ?? 0}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {formData?.tv?.targetGroup && (
                <Grid container spacing={2}>
                  <Box
                    width="100%"
                    borderBottom="1px solid #C7D8F9"
                    ml={3}
                    mr={1}
                    mt={2}
                    mb={2}
                  />
                  <Grid item xs={12}>
                    <Button
                      onClick={handleSetGrpsButtonClick}
                      disabled={!isTVConfigChanged}
                      sx={{
                        border: '1px solid #447EEB',
                        color: 'primary.dark',
                        borderRadius: '30px',
                        background: '#fff',
                        width: '144px',
                        height: '34px',
                        fontWeight: 700,
                      }}
                    >
                      Set my GRPs
                    </Button>
                  </Grid>
                  {formData?.tv?.grpTable?.weeks.length && (
                    <>
                      <Grid item xs={12}>
                        <GrpSettingsAccordion
                          isExpanded={!!formData?.tv.grpTable.weeks.length}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <GrpPercentageSettingsAccordion
                          isExpanded={!!formData?.tv.grpTable.weeks.length}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </form>
  );
}
